import {
  Divider,
  Grid,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import {
  DefaultCard,
  ErrorLabel,
  FormHookResult,
  FormValueTypes,
  RadioButtonGroup,
} from 'design-system';
import { useMemo } from 'react';

import booleanRadioData from 'src/constants/booleanRadioData';

function useCustomStyles() {
  const theme = useTheme();

  const useStyles = makeStyles({
    container: {
      position: 'relative',
      background: 'linear-gradient(#D1D0D0,#D1D0D0) center/1px 100% no-repeat',
      [theme.breakpoints.down('sm')]: {
        background: (): string => 'none',
      },
    },
  });

  return useStyles();
}

function getMappedBooleanValue(value: FormValueTypes): string | undefined {
  if (typeof value !== 'undefined') {
    return value ? 'yes' : 'no';
  }
  return '';
}

function shouldShowError(
  usedPepValue?: string,
  usedPepBloodRelated?: string,
  usedPepLawArticle?: string,
  cicacValue?: string,
): boolean {
  return (
    usedPepValue === 'yes' ||
    usedPepBloodRelated === 'yes' ||
    usedPepLawArticle === 'yes' ||
    cicacValue === 'yes'
  );
}

function showError(useLegalEntityLayout?: boolean): JSX.Element {
  return (
    <Grid item xs={12}>
      <br />
      <ErrorLabel
        data-testid={
          useLegalEntityLayout ? 'legal-person-error' : 'natural-person-error'
        }
      >
        La emisión (suscripción de póliza) deberá realizarse en nuestras
        oficinas. Por favor contacta a tu Corredor o a un Ejecutivo Comercial al
        800-800-ASSA (2772) para recibir asistencia.
      </ErrorLabel>
    </Grid>
  );
}

function makePepQuestion(
  getInputProps: FormHookResult,
  usedPepValue?: string,
  sharedDisabledValues?: string[],
  isDisabled?: boolean,
  isMobile?: boolean,
): JSX.Element {
  return (
    <>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="swi-radio-ispep"
          radioGroupName="pep"
          formTitle=""
          defaultValue={usedPepValue}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('pep').onChange(newVal.Value === 'yes');
          }}
          disabledValues={sharedDisabledValues}
          disabled={isDisabled}
          errorMessage={getInputProps('pep').errorText}
          centerLabels={false}
        />
      </Grid>
      {isMobile && (
        <>
          <br />
          <Divider orientation="horizontal" />
        </>
      )}
    </>
  );
}

function makeBloodRelatedToPepQuestion(
  getInputProps: FormHookResult,
  usedPepBloodRelated?: string,
  isDisabled?: boolean,
  isMobile?: boolean,
): JSX.Element {
  return (
    <>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="swi-radio-ispepbloodrelated"
          radioGroupName="bloodRelatedToPep"
          formTitle=""
          defaultValue={usedPepBloodRelated}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('bloodRelatedToPep').onChange(newVal.Value === 'yes');
          }}
          disabled={isDisabled}
          errorMessage={getInputProps('bloodRelatedToPep').errorText}
          centerLabels={false}
        />
      </Grid>
      {isMobile && (
        <>
          <br />
          <Divider orientation="horizontal" />
        </>
      )}
    </>
  );
}

function makePepLawArticleQuestion(
  getInputProps: FormHookResult,
  usedPepLawArticle?: string,
  isDisabled?: boolean,
  isMobile?: boolean,
): JSX.Element {
  return (
    <>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="swi-radio-ispeplawarticle"
          radioGroupName="pepLawArticle"
          formTitle=""
          defaultValue={usedPepLawArticle}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('pepLawArticle').onChange(newVal.Value === 'yes');
          }}
          disabled={isDisabled}
          errorMessage={getInputProps('pepLawArticle').errorText}
          centerLabels={false}
        />
      </Grid>
      {isMobile && (
        <>
          <br />
          <Divider orientation="horizontal" />
        </>
      )}
    </>
  );
}

function makeCicacQuestion(
  getInputProps: FormHookResult,
  cicacValue?: string,
  isDisabled?: boolean,
): JSX.Element {
  return (
    <Grid item xs={12}>
      <RadioButtonGroup
        inputId="swi-radio-iscicac"
        radioGroupName="cicac"
        formTitle=""
        defaultValue={cicacValue}
        radioData={booleanRadioData}
        onChange={(newVal): void => {
          getInputProps('cicac').onChange(newVal.Value === 'yes');
        }}
        disabled={isDisabled}
        errorMessage={getInputProps('cicac').errorText}
        centerLabels={false}
      />
    </Grid>
  );
}

function makeCollectiveQuestions(
  getInputProps: FormHookResult,
  sharedDisabledValues: string[],
  isDisabled: boolean,
): JSX.Element {
  const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
  const usedPepBloodRelated = getMappedBooleanValue(
    getInputProps('bloodRelatedToPep').value,
  );

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="caption" data-testid="legal-person-pep-label">
          ¿Alguno de los accionistas desempeña actualmente o ha desempeñado un
          puesto político o con influencia política en los últimos dos años?
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <RadioButtonGroup
          inputId="sales-web-inspection-radio-ispep"
          radioGroupName="pep"
          formTitle=""
          defaultValue={usedPepValue}
          radioData={booleanRadioData}
          onChange={(newVal): void => {
            getInputProps('pep').onChange(newVal.Value === 'yes');
          }}
          disabledValues={sharedDisabledValues}
          disabled={isDisabled}
          errorMessage={getInputProps('pep').errorText}
          centerLabels
        />
      </Grid>
      {usedPepValue === 'no' && typeof usedPepValue !== 'undefined' && (
        <>
          <Grid item xs={12}>
            <Typography variant="caption">
              ¿Alguno de los accionistas mantiene un grado de consanguinidad o
              afinidad con un PEP (Persona Políticamente Expuesta)?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <RadioButtonGroup
              inputId="sales-web-inspection-radio-ispepbloodrelated"
              radioGroupName="bloodRelatedToPep"
              formTitle=""
              defaultValue={usedPepBloodRelated}
              radioData={booleanRadioData}
              onChange={(newVal): void => {
                getInputProps('bloodRelatedToPep').onChange(
                  newVal.Value === 'yes',
                );
              }}
              disabled={isDisabled}
              errorMessage={getInputProps('bloodRelatedToPep').errorText}
              centerLabels
            />
          </Grid>
        </>
      )}
    </>
  );
}

type MakeLayoutProps = {
  /**  A helper method that lets you extract the required data for an specific input name */
  sharedDisabledValues: string[];
  /** Use when component needs to be disabled */
  getInputProps: FormHookResult;
  isDisabled: boolean;
  isMobile?: boolean;
  isIndividualQuotation?: boolean;
};

function makeLayoutForNaturalPerson({
  sharedDisabledValues,
  getInputProps,
  isDisabled,
  isMobile,
}: MakeLayoutProps): JSX.Element {
  const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
  const usedPepBloodRelated = getMappedBooleanValue(
    getInputProps('bloodRelatedToPep').value,
  );
  const cicacValue = getMappedBooleanValue(getInputProps('cicac').value);

  return (
    <>
      <Grid item xs={isMobile ? 12 : 6}>
        <Grid item xs={12}>
          <Typography variant="caption" data-testid="natural-person-pep-label">
            ¿Desempeña o ha desempeñado algún cargo político destacado (PEP) en
            territorio nacional o en el extranjero?
          </Typography>
        </Grid>
        {makePepQuestion(
          getInputProps,
          usedPepValue,
          sharedDisabledValues,
          isDisabled,
          isMobile,
        )}
      </Grid>

      <Grid item xs={isMobile ? 12 : 6}>
        <Grid item xs={12}>
          <Typography variant="caption">
            ¿Se desempeña entre las actividades de manejo de fondos de terceros
            físicos o jurídicos? (Artículo 15 de la ley 8204)
          </Typography>
        </Grid>
        {makeBloodRelatedToPepQuestion(
          getInputProps,
          usedPepBloodRelated,
          isDisabled,
          isMobile,
        )}
      </Grid>

      <Grid item xs={isMobile ? 12 : 6}>
        <Grid item xs={12}>
          <Typography variant="caption">
            ¿Desea que su información sea compartida en el CICAC (Centro de
            Información Conozca a su Cliente) de la SUGESE?
          </Typography>
        </Grid>
        {makeCicacQuestion(getInputProps, cicacValue, isDisabled)}
      </Grid>
    </>
  );
}

function makeLayoutForLegalPerson({
  sharedDisabledValues,
  getInputProps,
  isDisabled,
  isMobile,
  isIndividualQuotation,
}: MakeLayoutProps): JSX.Element {
  const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
  const usedPepBloodRelated = getMappedBooleanValue(
    getInputProps('bloodRelatedToPep').value,
  );
  const usedPepLawArticle = getMappedBooleanValue(
    getInputProps('pepLawArticle').value,
  );
  const cicacValue = getMappedBooleanValue(getInputProps('cicac').value);

  return (
    <>
      {isIndividualQuotation ? (
        <>
          <Grid item xs={isMobile ? 12 : 6}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                data-testid="legal-person-pep-label"
              >
                ¿Algún Socio, director o Representante de la empresa desempeña o
                ha desempeñado algún cargo político?
              </Typography>
            </Grid>
            {makePepQuestion(
              getInputProps,
              usedPepValue,
              sharedDisabledValues,
              isDisabled,
              isMobile,
            )}
          </Grid>

          <Grid item xs={isMobile ? 12 : 6}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                data-testid="legal-person-pep-blood-related-label"
              >
                ¿Algún socio o Representante de la empresa tiene relación
                directa (primer grado de consanguinidad) con alguna persona
                expuesta políticamente (PEP)?
              </Typography>
            </Grid>
            {makeBloodRelatedToPepQuestion(
              getInputProps,
              usedPepBloodRelated,
              isDisabled,
              isMobile,
            )}
          </Grid>

          <Grid item xs={isMobile ? 12 : 6}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                data-testid="legal-person-pep-law-article-label"
              >
                ¿Se desempeña entre las actividades citadas en el artículo 15 y
                Artículo 15 bis de la Ley 7786 (administración de fondos de
                terceros)?
              </Typography>
            </Grid>
            {makePepLawArticleQuestion(
              getInputProps,
              usedPepLawArticle,
              isDisabled,
              isMobile,
            )}
          </Grid>

          <Grid item xs={isMobile ? 12 : 6}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                data-testid="legal-person-cicac-label"
              >
                ¿Desea que su información sea compartida en el CICAC (Centro de
                Información Conozca a su Cliente) de la SUGESE?
              </Typography>
            </Grid>
            {makeCicacQuestion(getInputProps, cicacValue, isDisabled)}
          </Grid>
        </>
      ) : (
        makeCollectiveQuestions(getInputProps, sharedDisabledValues, isDisabled)
      )}
    </>
  );
}

type Props = {
  /**  A helper method that lets you extract the required data for an specific input name */
  getInputProps: FormHookResult;
  /** Use when component needs to be disabled */
  isDisabled: boolean;
  /** Set "true" for legal person layout */
  useLegalEntityLayout?: boolean;
  isMobile: boolean;
  isIndividualQuotation?: boolean;
};

const PepCard = ({
  getInputProps,
  isDisabled,
  useLegalEntityLayout,
  isMobile,
  isIndividualQuotation,
}: Props): JSX.Element => {
  const classes = useCustomStyles();
  const sharedDisabledValues = useMemo(
    () => (isDisabled ? ['yes', 'no'] : ['']),
    [isDisabled],
  );

  let mustShowError = false;

  if (isIndividualQuotation) {
    const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
    const usedPepBloodRelated = getMappedBooleanValue(
      getInputProps('bloodRelatedToPep').value,
    );
    const usedPepLawArticle = getMappedBooleanValue(
      getInputProps('pepLawArticle').value,
    );
    const cicacValue = getMappedBooleanValue(getInputProps('cicac').value);

    mustShowError = shouldShowError(
      usedPepValue,
      usedPepBloodRelated,
      usedPepLawArticle,
      cicacValue,
    );
  } else {
    const usedPepValue = getMappedBooleanValue(getInputProps('pep').value);
    const usedPepBloodRelated = getMappedBooleanValue(
      getInputProps('bloodRelatedToPep').value,
    );

    const cicacValue = getMappedBooleanValue(getInputProps('cicac').value);

    mustShowError = shouldShowError(
      usedPepValue,
      usedPepBloodRelated,
      cicacValue,
    );
  }

  return (
    <DefaultCard
      disabled={isDisabled}
      title={
        isIndividualQuotation
          ? 'Preguntas adicionales'
          : 'Persona Expuesta Políticamente'
      }
      data-testid="card"
    >
      <Grid
        container
        spacing={2}
        className={isIndividualQuotation ? classes.container : ''}
      >
        {useLegalEntityLayout
          ? makeLayoutForLegalPerson({
              sharedDisabledValues,
              getInputProps,
              isDisabled,
              isMobile,
              isIndividualQuotation,
            })
          : makeLayoutForNaturalPerson({
              sharedDisabledValues,
              getInputProps,
              isDisabled,
              isMobile,
            })}
      </Grid>
      {mustShowError && showError(useLegalEntityLayout)}
    </DefaultCard>
  );
};

export default PepCard;
